/**
 * /dashboard/accounting/items/$id — Item detail (G_ACC3b).
 *
 * Mirrors the contacts detail route: header + identifiers + an inline
 * edit mode toggled via state. Same role-gate + archive flow.
 *
 * Migration note (Phase 3e — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/accounting/items/[id]/page.tsx.
 */

import { useState } from "react";
import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight, Pencil, X } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useAccounts, useItem } from "@/hooks/use-accounting";
import {
  ITEM_KIND_LABELS,
  bpsToPercentString,
  type Item,
} from "@/types/accounting";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { ItemForm } from "@/components/accounting/item-form";
import { ArchiveItemButton } from "@/components/accounting/archive-item-button";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { formatDateTime } from "@/lib/format";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/items/$id",
)({
  component: ItemDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function FieldRow({
  label,
  value,
  mono,
}: Readonly<{
  label: string;
  value?: string | null;
  mono?: boolean;
}>) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={
          mono ? "font-mono text-sm text-gray-900" : "text-sm text-gray-900"
        }
      >
        {value && value.trim() !== "" ? value : "—"}
      </p>
    </div>
  );
}

function ItemDetailView({
  item,
  onEdit,
}: Readonly<{ item: Item; onEdit: () => void }>) {
  // Default accounts print as "4110 · Sales — Services" (the chart's own
  // code + name), not an internal id.
  const accountsQ = useAccounts({ include_archived: true });
  const accountLabel = (id: number | null | undefined, kind: "income" | "expense") => {
    if (!id) {
      return `Not set — each ${kind === "income" ? "invoice" : "bill"} line picks its account`;
    }
    const a = accountsQ.data?.items.find((x) => x.id === id);
    return a ? `${a.code} · ${a.name}` : "…";
  };
  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle className="text-xl">{item.name}</CardTitle>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="outline" className="font-mono">
                  {item.code}
                </Badge>
                <Badge variant="outline" className="font-medium">
                  {ITEM_KIND_LABELS[item.kind]}
                </Badge>
                {item.track_inventory && (
                  <Badge
                    variant="outline"
                    className="border-blue-300 bg-blue-50 text-blue-900"
                  >
                    Inventory
                  </Badge>
                )}
                {item.is_archived && (
                  <Badge
                    variant="outline"
                    className="border-transparent bg-muted text-muted-foreground"
                  >
                    Archived
                  </Badge>
                )}
                <span>Updated {formatDateTime(item.updated_at)}</span>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={onEdit}
              >
                <Pencil aria-hidden className="h-3.5 w-3.5" />
                Edit
              </Button>
              {!item.is_archived && <ArchiveItemButton item={item} />}
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Tax & Classification</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <FieldRow
            label="Default Tax Rate"
            value={`${bpsToPercentString(item.default_tax_rate_bps)}%`}
          />
          <FieldRow
            label={item.kind === "service" ? "SAC" : "HSN"}
            value={item.hsn_sac}
            mono
          />
          <FieldRow
            label="Track Inventory"
            value={item.track_inventory ? "Yes" : "No"}
          />
          <FieldRow
            label="Default Income Account"
            value={accountLabel(item.default_income_account_id, "income")}
          />
          <FieldRow
            label="Default Expense Account"
            value={accountLabel(item.default_expense_account_id, "expense")}
          />
        </CardContent>
      </Card>

      {item.description && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm text-gray-900">
              {item.description}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ItemDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = useItem(numId, validId);

  const [editing, setEditing] = useState(false);

  return (
    <AccountingModuleShell
      title={q.data?.name ?? "Item"}
      actions={
        <Button asChild variant="outline" className="gap-1.5 lg:hidden">
          <Link to="/app/$tenantSlug/accounting/items" params={{ tenantSlug }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid item id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid item id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this item</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/accounting/items" params={{ tenantSlug }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to Items
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && q.data && !editing && (
        <ItemDetailView item={q.data} onEdit={() => setEditing(true)} />
      )}

      {validId && q.data && editing && (
        <Card className="max-w-3xl">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-base">Edit Item</CardTitle>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
                aria-label="Close edit form"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ItemForm
              item={q.data}
              onSuccess={() => setEditing(false)}
              onCancel={() => setEditing(false)}
            />
          </CardContent>
        </Card>
      )}
    </AccountingModuleShell>
  );
}
